import { useState, useEffect } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { authService, userService } from '../services';
import { UserProfile, AuthState } from '../types';

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    console.log('[USE_AUTH] Configurando observador de cambios de autenticación');
    const unsubscribe = authService.onAuthStateChange(async (firebaseUser: FirebaseUser | null) => {
      console.log('[USE_AUTH] Cambio de estado de autenticación detectado');
      if (firebaseUser) {
        console.log('[USE_AUTH] Usuario autenticado, UID:', firebaseUser.uid);
        console.log('[USE_AUTH] Email:', firebaseUser.email);
        console.log('[USE_AUTH] Email verificado:', firebaseUser.emailVerified);
        
        // Usuario autenticado, obtener perfil de Firestore
        const userProfileResult = await userService.getUserProfile(firebaseUser.uid);
        console.log('[USE_AUTH] Resultado de obtener perfil:', userProfileResult);
        
        if (userProfileResult.success && userProfileResult.data) {
          setAuthState({
            user: userProfileResult.data,
            loading: false,
            error: null
          });
          
          // Actualizar último login
          userService.updateLastLogin(firebaseUser.uid);
        } else {
          console.log('[USE_AUTH] Perfil no encontrado, creando nuevo perfil');
          // Si no existe el perfil, crearlo
          const createResult = await userService.createUserProfile(
            firebaseUser.uid,
            firebaseUser.email || '',
            firebaseUser.displayName || undefined
          );
          console.log('[USE_AUTH] Resultado de crear perfil:', createResult);
          
          if (createResult.success && createResult.data) {
            setAuthState({
              user: createResult.data,
              loading: false,
              error: null
            });
          } else {
            console.error('[USE_AUTH] Error al crear perfil de usuario');
            setAuthState({
              user: null,
              loading: false,
              error: 'Error al crear perfil de usuario'
            });
          }
        }
      } else {
        console.log('[USE_AUTH] Usuario no autenticado');
        // Usuario no autenticado
        setAuthState({
          user: null,
          loading: false,
          error: null
        });
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    const result = await authService.login(email, password);
    
    if (result.success) {
      // El hook useEffect manejará la actualización del estado
      return { success: true };
    } else {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: result.error
      }));
      return { 
        success: false, 
        error: result.error,
        needsVerification: result.needsVerification 
      };
    }
  };

  const register = async (email: string, password: string, profileType: 'tutor' | 'child' = 'tutor') => {
    console.log('[USE_AUTH] Iniciando registro para email:', email, 'Tipo:', profileType);
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    const result = await authService.register(email, password);
    console.log('[USE_AUTH] Resultado del registro:', result);
    
    if (result.success) {
      // Crear perfil de usuario en Firestore
      if (result.user) {
        console.log('[USE_AUTH] Creando perfil de usuario en Firestore, UID:', result.user.uid);
        await userService.createUserProfile(
          result.user.uid,
          result.user.email || '',
          result.user.displayName || undefined,
          profileType
        );
        console.log('[USE_AUTH] Perfil de usuario creado exitosamente');
      }
      setAuthState(prev => ({ ...prev, loading: false }));
      return { success: true, message: result.message };
    } else {
      console.error('[USE_AUTH] Error en registro:', result.error);
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: result.error
      }));
      return { success: false, error: result.error };
    }
  };

  const logout = async () => {
    setAuthState(prev => ({ ...prev, loading: true }));
    
    const result = await authService.logout();
    
    if (result.success) {
      setAuthState({
        user: null,
        loading: false,
        error: null
      });
      return { success: true };
    } else {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: result.error
      }));
      return { success: false, error: result.error };
    }
  };

  const resendVerificationEmail = async () => {
    console.log('[USE_AUTH] Iniciando reenvío de email de verificación');
    const result = await authService.resendVerificationEmail();
    console.log('[USE_AUTH] Resultado del reenvío:', result);
    return result;
  };

  const resetPassword = async (email: string) => {
    console.log('[USE_AUTH] Iniciando recuperación de contraseña para email:', email);
    const result = await authService.resetPassword(email);
    console.log('[USE_AUTH] Resultado de recuperación:', result);
    return result;
  };

  const verifyPasswordResetCode = async (code: string) => {
    console.log('[USE_AUTH] Verificando código de reset de contraseña');
    const result = await authService.verifyPasswordResetCode(code);
    console.log('[USE_AUTH] Resultado de verificación:', result);
    return result;
  };

  const confirmPasswordReset = async (code: string, newPassword: string) => {
    console.log('[USE_AUTH] Confirmando reset de contraseña');
    const result = await authService.confirmPasswordReset(code, newPassword);
    console.log('[USE_AUTH] Resultado de confirmación:', result);
    return result;
  };

  const loginWithGoogle = async () => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    const result = await authService.loginWithGoogle();
    
    if (result.success) {
      // Crear perfil de usuario en Firestore si no existe
      if (result.user) {
        const existingProfile = await userService.getUserProfile(result.user.uid);
        if (!existingProfile.success) {
          await userService.createUserProfile(
            result.user.uid,
            result.user.email || '',
            result.user.displayName || undefined
          );
        }
      }
      return { success: true, message: result.message };
    } else {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: result.error
      }));
      return { success: false, error: result.error };
    }
  };

  return {
    ...authState,
    login,
    register,
    logout,
    resendVerificationEmail,
    resetPassword,
    verifyPasswordResetCode,
    confirmPasswordReset,
    loginWithGoogle
  };
};
